import {
    Action,
    CaseReducerActions,
    SliceCaseReducers,
    createSlice,
} from '@reduxjs/toolkit'
import {
    ninchatEventExtraActions,
    ninchatEventExtraReducers,
    ninchatEventReducers,
} from './event'
import {
    setSlice,
    sliceFilters,
} from '../store/storage'
import {NinchatState} from './types'
import {ninchatActionExtraReducers} from './action'
import {startAppListening} from '../middlewares/listener'
import {updateNinchatStateReducer} from './reducers/updateNinchatState'

export const initialState: NinchatState = {
    _audience_transcripts: {},
    _files: {},
    _messages: {},
    _queue_transcripts: {},
    _queues: {},
    _realms: {},
    _session_revision: 0,
    _tags: {},
    _user_audience_comments: {},
    _user_audience_transcripts: {}, // Based on user_transcripts
    _user_queues_activity: {},
    _user_queues_stats: {},
    _user_realm_queues: {},
    _users: {},
    conn_state: null,
    puppet_masters: {},
    session_host: null,
    session_id: null,
    session_realms_permissions: {},
    user_account: {},
    user_attrs: {},
    user_auth: undefined,
    user_channels: {},
    user_dialogues: {},
    user_id: null,
    user_identities: {},
    user_queues: {},
    user_realms: {},
    user_realms_member: {},
    user_settings: {},
    user_sso: {},
}

const ninchatActionSlice = createSlice({
    extraReducers: ninchatActionExtraReducers,
    initialState,
    name: 'NINCHAT/action',
    reducers: {},
})

export const ninchatEventSlice = createSlice({
    extraReducers: ninchatEventExtraReducers,
    initialState,
    name: 'NINCHAT/event',
    reducers: ninchatEventReducers,
})

const ninchatSlice = createSlice({
    extraReducers: (builder) => {
        builder
            .addMatcher(
                (action): action is Action => action.type.startsWith(`${ninchatActionSlice.name}/`),
                (state, action) => ninchatActionSlice.reducer(state, action),
            )
            .addMatcher(
                (action): action is Action => action.type.startsWith(`${ninchatEventSlice.name}/`),
                (state, action) => ninchatEventSlice.reducer(state, action),
            )
    },
    initialState,
    name: 'NINCHAT',
    reducers: {updateNinchatState: updateNinchatStateReducer},
})

sliceFilters.set('ninchat', {
    user_auth: true,
    user_id: true,
    user_settings: {
        chat_density: true,
        language: true,
        theme: true,
    },
})

startAppListening({
    effect: (_action, listenerApi) => setSlice({
        ninchat: {
            user_auth: listenerApi.getState().ninchat.user_auth,
            user_id: listenerApi.getState().ninchat.user_id,
            user_settings: {
                chat_density: listenerApi.getState().ninchat.user_settings.chat_density,
                language: listenerApi.getState().ninchat.user_settings.language,
                theme: listenerApi.getState().ninchat.user_settings.theme,
            },
        } as NinchatState,
    }),
    predicate: (action) => typeof action === 'object' && action.type.startsWith('NINCHAT/'),
})

export const {reducer: ninchatReducer} = ninchatSlice

export const ninchatEventActions: CaseReducerActions<SliceCaseReducers<NinchatState>, 'NINCHAT/event'> = {
    ...ninchatEventSlice.actions,
    ...ninchatEventExtraActions,
}

export {ninchatEventExtraActions}
export const {updateNinchatState} = ninchatSlice.actions
